import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/src/components/ui/dialog';
import { Button } from '@/src/components/ui/button';
import { Input } from '@/src/components/ui/input';
import { Label } from '@/src/components/ui/label';
import { Badge } from '@/src/components/ui/badge';
import { Layers, Plus, Trash2, X, Link as LinkIcon, Edit2, Search } from 'lucide-react';

export interface GlobalAlias {
  id: string;
  name: string;
  linkedFieldIds: string[];
}

interface AliasBuilderProps {
  open: boolean;
  onClose: () => void;
  globalAliases: GlobalAlias[];
  setGlobalAliases: React.Dispatch<React.SetStateAction<GlobalAlias[]>>;
  allFields: any[];
}

export default function AliasBuilder({ open, onClose, globalAliases, setGlobalAliases, allFields }: AliasBuilderProps) {
  const [editingAlias, setEditingAlias] = useState<GlobalAlias | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredFields = useMemo(() => {
    if (!searchTerm) return allFields;
    const lower = searchTerm.toLowerCase();
    return allFields.filter((f) => 
      f.fieldLabel.toLowerCase().includes(lower) || 
      f.formTitle.toLowerCase().includes(lower)
    );
  }, [allFields, searchTerm]);

  const handleSaveAlias = () => {
    if (!editingAlias || !editingAlias.name.trim()) return;
    
    setGlobalAliases((prev: GlobalAlias[]) => {
      const idx = prev.findIndex(a => a.id === editingAlias.id);
      if (idx > -1) {
        const next = [...prev];
        next[idx] = editingAlias;
        return next;
      }
      return [...prev, editingAlias];
    });
    
    setEditingAlias(null);
  };

  const handleRemoveAlias = (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este Agrupador Global?')) return;
    setGlobalAliases(globalAliases.filter(a => a.id !== id));
  };

  const toggleFieldLink = (fieldId: string) => {
    if (!editingAlias) return;
    setEditingAlias(prev => {
      if (!prev) return prev;
      const linked = prev.linkedFieldIds.includes(fieldId)
        ? prev.linkedFieldIds.filter(id => id !== fieldId)
        : [...prev.linkedFieldIds, fieldId];
      return { ...prev, linkedFieldIds: linked };
    });
  };

  return (
    <Dialog open={open} onOpenChange={(val) => !val && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col p-0 overflow-hidden bg-gray-50">
        <DialogHeader className="p-5 border-b bg-white shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 text-blue-700 rounded-lg">
              <Layers size={20} />
            </div>
            <div>
              <DialogTitle className="text-xl">Agrupadores Globais</DialogTitle>
              <p className="text-sm text-gray-500 mt-1">
                Una perguntas equivalentes de diferentes formulários em uma única variável global.
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="flex flex-1 min-h-0">
          {/* Left Panel: List of Aliases */}
          <div className="w-1/3 border-r bg-white p-4 flex flex-col gap-4 overflow-y-auto">
            <Button 
              onClick={() => setEditingAlias({ id: `alias_${Date.now()}`, name: 'Novo Agrupador', linkedFieldIds: [] })}
              className="w-full gap-2 bg-blue-600 hover:bg-blue-700"
            >
              <Plus size={16} /> Novo Agrupador
            </Button>

            <div className="flex flex-col gap-2 mt-2">
              {globalAliases.length === 0 && (
                <div className="text-center text-sm text-gray-400 py-6">
                  Nenhum agrupador criado.
                </div>
              )}
              {globalAliases.map(alias => (
                <div 
                  key={alias.id}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors group relative ${editingAlias?.id === alias.id ? 'bg-blue-50 border-blue-200' : 'bg-white hover:bg-gray-50 border-gray-200'}`}
                  onClick={() => setEditingAlias({...alias})}
                >
                  <div className="font-semibold text-sm text-gray-800">{alias.name}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {alias.linkedFieldIds?.length || 0} pergunta(s) vinculada(s)
                  </div>
                  <button 
                    onClick={(e) => { e.stopPropagation(); handleRemoveAlias(alias.id); }}
                    className="absolute top-3 right-3 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Right Panel: Edit Area */}
          <div className="w-2/3 flex flex-col bg-gray-50 overflow-hidden">
            {editingAlias ? (
              <div className="flex flex-col h-full">
                <div className="p-5 border-b bg-white shrink-0 space-y-4">
                  <div>
                    <Label className="text-xs font-semibold text-gray-600 uppercase">Nome do Agrupador</Label>
                    <Input 
                      className="mt-1 font-semibold text-lg" 
                      value={editingAlias.name}
                      onChange={e => setEditingAlias({...editingAlias, name: e.target.value})}
                      placeholder="Ex: Placa do Veículo"
                    />
                  </div>
                </div>

                <div className="p-5 flex-1 overflow-y-auto space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold text-gray-800">Perguntas Vinculadas</Label>
                    <Badge variant="secondary" className="bg-blue-100 text-blue-700">{editingAlias.linkedFieldIds.length} Selecionadas</Badge>
                  </div>
                  
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input 
                      placeholder="Buscar perguntas por nome ou formulário..." 
                      className="pl-9"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    {filteredFields.map(field => {
                      // Se o field já for um agrupador global ou um custom parameter, ignoramos para evitar recursão
                      if (field.formId === 'global') return null;

                      const isSelected = editingAlias.linkedFieldIds.includes(field.fieldId);
                      
                      return (
                        <div 
                          key={`${field.formId}-${field.fieldId}`}
                          onClick={() => toggleFieldLink(field.fieldId)}
                          className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${isSelected ? 'bg-blue-50 border-blue-200' : 'bg-white hover:bg-gray-50 border-gray-200'}`}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={`p-1.5 rounded-md ${isSelected ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                              <LinkIcon size={14} />
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-gray-800 truncate">{field.fieldLabel}</div>
                              <div className="text-[11px] text-gray-500 truncate">{field.formTitle}</div>
                            </div>
                          </div>
                          
                          <div className="shrink-0">
                            <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                              {isSelected && <div className="w-2 h-2 bg-white rounded-full" />}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {filteredFields.length === 0 && (
                      <div className="text-center py-4 text-sm text-gray-500">Nenhuma pergunta encontrada.</div>
                    )}
                  </div>
                </div>

                <div className="p-4 bg-white border-t shrink-0 flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setEditingAlias(null)}>Cancelar</Button>
                  <Button onClick={handleSaveAlias} className="bg-blue-600 hover:bg-blue-700">Salvar Agrupador</Button>
                </div>
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-gray-400 p-8 text-center">
                <Layers size={48} className="mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold text-gray-600 mb-2">Nenhum Agrupador Selecionado</h3>
                <p className="text-sm">Selecione um agrupador na lista ao lado ou crie um novo para vincular perguntas.</p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
